import { type JSX, type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { AppLink, useNavigator } from '../../discovery/router';
import { DestinationLink } from '../../components/primitives';
import { helpTopicPath, routes } from '../../routes';
import { useSession } from '../../session/SessionProvider';
import { type Address, getProfile } from '../account/account-api';
import { type Order, type Tracking } from '../orders/orders-api';
import { type Step, money, shopOf, stepsOf } from '../orders/order-model';
import { LoginLink } from '../panels';
import { rememberReturnPath } from '../preferences';
import {
  type Requirement,
  type Store,
  SessionApiError,
  getCart,
  getRequirement,
  getStore,
} from '../store/store-api';
import { initialsOf } from '../store/store-model';
import {
  type CheckoutSession,
  type DeliveryReach,
  type Fulfilment,
  type PaymentsConfig,
  type Quote,
  confirmCheckout,
  deliveryReach,
  getOrder,
  getOrderTracking,
  getPaymentsConfig,
  getPennyQuote,
  setAddress,
  setContact,
  setFulfilment,
  startCheckout,
  startPayment,
} from './checkout-api';
import {
  type AddressFields,
  type FieldErrors,
  UK_POSTCODE,
  addressErrors,
  attemptKey,
  collectsFrom,
  deliversFrom,
  detailErrors,
  forgetAttempt,
  paymentOutcome,
  polling,
  priceChange,
  shopAddress,
  suppliedBy,
  thanksTitle,
} from './checkout-model';
import { STRIPE_APPEARANCE, type StripeElements, type StripeJs, loadStripe } from './payment-element';
import './checkout.css';

/**
 * The Checkout page — the `Just Treated Checkout` frame, on the platform's own
 * checkout, payment and order routes (website stage 6).
 *
 * ## What is real here
 *
 * The basket, the quote (items, delivery fee, total), whether the shop
 * delivers to the address, the order, the payment and its outcome are all the
 * platform's. The page never adds anything up and never says "paid" on its
 * own: after a payment starts it polls the order and waits for the gateway's
 * signed webhook to be reflected there, exactly as the Android app does. A
 * wait that runs out says "still confirming", never "failed".
 *
 * ## Who may check out
 *
 * A guest may buy what needs no consultation: the basket and the order belong
 * to this browser's device handle, as the Android app's do. A line answered on
 * a consultation, or a prescription-only medicine, needs an account — the
 * frame's "You need an account for this order" — and Log in returns here with
 * the same basket, because the basket is the device's, not the session's.
 *
 * ## What the frame draws that the platform does not have
 *
 * Saved cards, Apple Pay and Google Pay rows, a promo code, account credit,
 * voucher stamps, scheduled delivery slots, a courier note, the out-of-stock
 * preference, service and small-order fees and the emailed receipt have
 * nothing behind them. The interactive ones say "Coming soon" and do nothing;
 * the numbers are simply not drawn (owner decisions D2, D3, D6, D8).
 */

type Mode = Fulfilment;

const NARROW = '(max-width: 939px)';

function useNarrow(): boolean {
  const read = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia(NARROW).matches;
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(NARROW);
    const follow = (): void => setNarrow(list.matches);
    list.addEventListener?.('change', follow);
    return () => list.removeEventListener?.('change', follow);
  }, []);
  return narrow;
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const ICON = {
  back: 'M14.6 5.4 8 12l6.6 6.6',
  lock: 'M5 11h14v10H5zM8 11V7.5a4 4 0 0 1 8 0V11',
  warn: 'M12 3.5 2.5 20h19ZM12 10v4.5M12 17.2h.01',
  home: 'M4 11 12 4l8 7v9h-5v-6H9v6H4Z',
  pin: 'M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21ZM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  truck: 'M3 16V8h11v8M14 11h4l3 3v2h-7M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  store: 'M3 10h18l-1.5-5h-15L3 10ZM5 10v10h14V10M10 20v-5h4v5',
  tag: 'M3.5 12.6V4.5h8.1l8.9 8.9-8.1 8.1ZM8 9a1.3 1.3 0 1 0 0-.01',
  star: 'M12 3.5 14.2 9l5.8.4-4.4 3.8 1.4 5.6L12 15.8l-5 3 1.4-5.6L4 9.4 9.8 9Z',
  check: 'm5 12.5 4.2 4.2L19 7',
  person: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 20a7.5 7.5 0 0 1 15 0',
  plus: 'M12 5v14M5 12h14',
};

function Svg({
  d,
  size = 16,
  stroke = 'currentColor',
  width = 2,
}: {
  readonly d: string;
  readonly size?: number;
  readonly stroke?: string;
  readonly width?: number;
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

function Soon(): JSX.Element {
  return <span className="jt-co-soon">Coming soon</span>;
}

function StepNumber({ n, done }: { readonly n: number; readonly done: boolean }): JSX.Element {
  return (
    <span className={`jt-co-num${done ? ' jt-co-num--done' : ''}`} aria-hidden="true">
      {done ? '✓' : n}
    </span>
  );
}

function Radio({ on }: { readonly on: boolean }): JSX.Element {
  return (
    <span aria-hidden="true" className={`jt-co-ring${on ? ' jt-co-ring--on' : ''}`}>
      <span />
    </span>
  );
}

function Field({
  label,
  value,
  onChange,
  error,
  type = 'text',
  autoComplete,
  inputMode,
  placeholder,
  wide = false,
  onBlur,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error?: string;
  readonly type?: string;
  readonly autoComplete?: string;
  readonly inputMode?: 'text' | 'email' | 'tel' | 'numeric';
  readonly placeholder?: string;
  readonly wide?: boolean;
  readonly onBlur?: () => void;
}): JSX.Element {
  const id = useId();
  const bad = error !== undefined && error !== '';
  return (
    <div className={`jt-co-field${wide ? ' jt-co-field--wide' : ''}`}>
      <label htmlFor={id} className="jt-co-field__label">
        {label}
      </label>
      <input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        {...(onBlur !== undefined ? { onBlur } : {})}
        type={type}
        {...(autoComplete !== undefined ? { autoComplete } : {})}
        {...(inputMode !== undefined ? { inputMode } : {})}
        {...(placeholder !== undefined ? { placeholder } : {})}
        aria-invalid={bad}
        {...(bad ? { 'aria-describedby': `${id}-err` } : {})}
        className={`jt-co-input${bad ? ' jt-co-input--bad' : ''}`}
      />
      {bad ? (
        <span id={`${id}-err`} className="jt-co-field__err">
          {error}
        </span>
      ) : null}
    </div>
  );
}

// -- The summary (the frame's right-hand aside) -------------------------------------

interface SummaryLine {
  readonly id: string;
  readonly title: string;
  readonly quantity: number;
  readonly lineTotalMinor: number;
  readonly imageUrl?: string;
  readonly consulted: boolean;
}

interface SummaryFigures {
  readonly lines: readonly SummaryLine[];
  readonly itemsTotalMinor: number;
  readonly deliveryFeeMinor: number;
  readonly totalMinor: number;
  readonly currency: string;
  readonly fulfilment: Mode;
  /** False while delivery has no quoted address: the fee is not known yet. */
  readonly feeKnown: boolean;
  readonly discountMinor?: number;
}

function figuresOfQuote(quote: Quote, fulfilment: Mode, feeKnown: boolean): SummaryFigures {
  return {
    lines: quote.lines.map((line) => ({
      id: line.lineId,
      title: line.title,
      quantity: line.quantity,
      lineTotalMinor: line.lineTotalMinor,
      ...(line.imageUrl !== undefined ? { imageUrl: line.imageUrl } : {}),
      consulted: line.consultationId !== undefined,
    })),
    itemsTotalMinor: quote.itemsTotalMinor,
    deliveryFeeMinor: quote.deliveryFeeMinor,
    totalMinor: quote.totalMinor,
    currency: quote.currency,
    fulfilment,
    feeKnown: fulfilment === 'COLLECTION' || feeKnown,
  };
}

function figuresOfOrder(order: Order): SummaryFigures {
  const discount = order.rewards?.redemption?.discountMinor;
  return {
    lines: order.lines.map((line) => ({
      id: line.id,
      title: line.title,
      quantity: line.quantity,
      lineTotalMinor: line.lineTotalMinor,
      ...(line.imageUrl !== undefined ? { imageUrl: line.imageUrl } : {}),
      consulted: line.consultationId !== undefined,
    })),
    itemsTotalMinor: order.itemsTotalMinor,
    deliveryFeeMinor: order.deliveryFeeMinor,
    totalMinor: order.totalMinor,
    currency: order.currency,
    fulfilment: order.fulfilment,
    feeKnown: true,
    ...(discount !== undefined && discount > 0 ? { discountMinor: discount } : {}),
  };
}

function rowsOf(f: SummaryFigures): readonly { k: string; v: string; green?: boolean }[] {
  const rows: { k: string; v: string; green?: boolean }[] = [
    { k: 'Subtotal', v: money(f.itemsTotalMinor, f.currency) },
  ];
  if (f.discountMinor !== undefined)
    rows.push({ k: 'PENNY', v: `−${money(f.discountMinor, f.currency)}`, green: true });
  if (f.fulfilment === 'DELIVERY') {
    rows.push({
      k: 'Delivery fee',
      v: !f.feeKnown
        ? 'Add your address'
        : f.deliveryFeeMinor > 0
          ? money(f.deliveryFeeMinor, f.currency)
          : 'Free',
    });
  }
  return rows;
}

function totalLabel(f: SummaryFigures): string {
  return f.feeKnown ? money(f.totalMinor, f.currency) : `${money(f.itemsTotalMinor, f.currency)} + delivery`;
}

function Summary({
  shopName,
  shopInitials,
  storeHref,
  modeLine,
  figures,
  signedIn,
  earn,
  guestNudge,
  promo,
  placeButton,
  supplied,
}: {
  readonly shopName: string;
  readonly shopInitials: string;
  readonly storeHref: string | undefined;
  readonly modeLine: string;
  readonly figures: SummaryFigures;
  readonly signedIn: boolean;
  readonly earn: number | undefined;
  readonly guestNudge: boolean;
  readonly promo: boolean;
  readonly placeButton: ReactNode;
  readonly supplied: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const shown = open ? figures.lines : figures.lines.slice(0, 3);
  return (
    <aside aria-label="Order summary" className="jt-co-sum">
      <div className="jt-co-sum__head">
        <span className="jt-co-tile">{shopInitials}</span>
        <span className="jt-co-sum__shop">
          <span className="jt-co-sum__name">{shopName}</span>
          <span className="jt-co-sum__mode">{modeLine}</span>
        </span>
        {storeHref !== undefined ? (
          <AppLink to={storeHref} className="jt-co-sum__edit">
            Edit
          </AppLink>
        ) : null}
      </div>
      <div className="jt-co-sum__items">
        {shown.map((line) => (
          <div key={line.id} className="jt-co-item">
            <span
              className="jt-co-item__img"
              style={line.imageUrl !== undefined ? { backgroundImage: `url("${line.imageUrl}")` } : undefined}
            >
              <span className="jt-co-item__qty">{line.quantity}</span>
            </span>
            <span className="jt-co-item__text">
              <span className="jt-co-item__name">{line.title}</span>
              {line.consulted ? <span className="jt-co-item__meta">Pharmacist-checked</span> : null}
            </span>
            <span className="jt-co-item__total">{money(line.lineTotalMinor, figures.currency)}</span>
          </div>
        ))}
        {figures.lines.length > 3 ? (
          <button type="button" className="jt-co-link" onClick={() => setOpen(!open)}>
            {open ? 'Show less' : `Show all ${String(figures.lines.length)} items`}
          </button>
        ) : null}
      </div>
      {promo ? (
        <div className="jt-co-sum__extras">
          <div className="jt-co-extra" aria-disabled="true">
            <Svg d={ICON.tag} size={18} stroke="#5b2ede" />
            <span className="jt-co-extra__label">Add a promo code</span>
            <Soon />
          </div>
          {signedIn ? (
            <div className="jt-co-extra" aria-disabled="true">
              <span aria-hidden="true" className="jt-co-coin">
                JT
              </span>
              <span className="jt-co-extra__label">Use account credit</span>
              <Soon />
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="jt-co-sum__foot">
        {rowsOf(figures).map((row) => (
          <div key={row.k} className="jt-co-row">
            <span>{row.k}</span>
            <span className={row.green === true ? 'jt-co-row__green' : 'jt-co-row__v'}>{row.v}</span>
          </div>
        ))}
        <div className="jt-co-total">
          <span>Total</span>
          <span>{totalLabel(figures)}</span>
        </div>
        {signedIn && earn !== undefined && earn > 0 ? (
          <span className="jt-co-earn">
            <Svg d={ICON.star} size={14} width={2.2} />
            You&rsquo;ll earn {earn} PENNY
          </span>
        ) : null}
        {guestNudge ? (
          <span className="jt-co-nudge">
            <LoginLink className="jt-co-nudge__link">Log in</LoginLink> to collect PENNY points on this order.
          </span>
        ) : null}
        {placeButton}
        <p className="jt-co-legal">
          By placing this order you agree to our{' '}
          <DestinationLink destination={routes.terms}>Terms</DestinationLink> and{' '}
          <DestinationLink destination={routes.privacy}>Privacy notice</DestinationLink>. {supplied}
        </p>
      </div>
    </aside>
  );
}

// -- The page ---------------------------------------------------------------------------

type Loaded =
  | { readonly kind: 'loading' }
  | { readonly kind: 'empty' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'ready' };

type ReachState =
  | { readonly status: 'idle' }
  | { readonly status: 'checking'; readonly postcode: string }
  | { readonly status: 'yes' | 'no'; readonly postcode: string; readonly reach: DeliveryReach }
  | { readonly status: 'unknown' | 'malformed' | 'failed'; readonly postcode: string };

type Finish =
  | { readonly kind: 'done'; readonly order: Order; readonly tracking: Tracking | undefined }
  | { readonly kind: 'pending'; readonly order: Order }
  | { readonly kind: 'closed'; readonly order: Order };

interface PriceModal {
  readonly name?: string;
  readonly oldMinor?: number;
  readonly nowMinor?: number;
  readonly totalMinor: number;
  readonly currency: string;
}

interface Declined {
  readonly title: string;
  readonly copy: string;
}

const DECLINED: Declined = {
  title: 'Your payment was declined',
  copy: "You haven't been charged. Check your card details, or try another card or payment method.",
};

function normalisePostcode(value: string): string {
  return value.trim().toUpperCase().replace(/\s+/g, '');
}

export function CheckoutPage(): JSX.Element {
  const navigator = useNavigator();
  const session = useSession();
  const narrow = useNarrow();
  const resumeOrderId = navigator.location.query.get('order') ?? undefined;
  const wantedMode = navigator.location.query.get('mode');
  const redirectStatus = navigator.location.query.get('redirect_status');

  const signedIn = session.status === 'signedIn' && session.user !== undefined;
  const sessionKnown =
    session.status === 'signedIn' || session.status === 'signedOut' || session.status === 'unreachable';

  const [loaded, setLoaded] = useState<Loaded>({ kind: 'loading' });
  const [checkout, setCheckout] = useState<CheckoutSession | undefined>(undefined);
  const [store, setStore] = useState<Store | undefined>(undefined);
  const [config, setConfig] = useState<PaymentsConfig | undefined>(undefined);
  const [requirements, setRequirements] = useState<ReadonlyMap<string, Requirement>>(new Map());
  const [addresses, setAddresses] = useState<readonly Address[]>([]);
  const [profileName, setProfileName] = useState<string | undefined>(undefined);
  const [profilePhone, setProfilePhone] = useState<string | undefined>(undefined);
  const [earn, setEarn] = useState<number | undefined>(undefined);

  const [mode, setModeState] = useState<Mode>('COLLECTION');
  const [guest, setGuest] = useState({ name: '', email: '', phone: '' });
  const [memberName, setMemberName] = useState('');
  const [addressChoice, setAddressChoice] = useState<string>('new');
  const [typed, setTyped] = useState<AddressFields>({ a1: '', a2: '', town: '', pc: '' });
  const [reach, setReach] = useState<ReachState>({ status: 'idle' });
  const [quotedFor, setQuotedFor] = useState<string | undefined>(undefined);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [acctError, setAcctError] = useState(false);
  const [banner, setBanner] = useState<string | undefined>(undefined);

  const [order, setOrder] = useState<Order | undefined>(undefined);
  const [placedRef, setPlacedRef] = useState<{ id: string; reference: string } | undefined>(undefined);
  const [proc, setProc] = useState<string | undefined>(undefined);
  const [declined, setDeclined] = useState<Declined | undefined>(undefined);
  const [priceModal, setPriceModal] = useState<PriceModal | undefined>(undefined);
  const [finish, setFinish] = useState<Finish | undefined>(undefined);
  const [toast, setToast] = useState('');
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const stripeRef = useRef<{ stripe: StripeJs; elements: StripeElements } | undefined>(undefined);
  const cardHost = useRef<HTMLDivElement | null>(null);
  const [cardState, setCardState] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle');

  const flash = useCallback((message: string) => {
    if (toastTimer.current !== undefined) clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(''), 2600);
  }, []);
  useEffect(
    () => () => {
      if (toastTimer.current !== undefined) clearTimeout(toastTimer.current);
    },
    [],
  );

  // -- Loading -------------------------------------------------------------------------

  useEffect(() => {
    let live = true;
    getPaymentsConfig()
      .then((found) => {
        if (live) setConfig(found);
      })
      .catch(() => {
        if (live) setConfig({ available: false, gateway: 'none', testMode: false });
      });
    return () => {
      live = false;
    };
  }, []);

  const loadStoreBySlug = useCallback(async (slug: string | undefined): Promise<Store | undefined> => {
    if (slug === undefined) return undefined;
    try {
      return await getStore(slug);
    } catch {
      return undefined;
    }
  }, []);

  // A placed order this tab is paying for (after a redirect, a reload, or "Pay now" from Orders).
  useEffect(() => {
    if (resumeOrderId === undefined || !sessionKnown) return undefined;
    let live = true;
    void (async () => {
      try {
        const found = await getOrder(resumeOrderId);
        if (!live) return;
        setOrder(found);
        setPlacedRef({ id: found.id, reference: found.reference });
        setStore(await loadStoreBySlug(found.storefront?.slug));
        const outcome = paymentOutcome(found);
        if (outcome === 'PAID') {
          const tracking = await getOrderTracking(found.id).catch(() => undefined);
          if (live) setFinish({ kind: 'done', order: found, tracking });
        } else if (outcome === 'CLOSED') {
          setFinish({ kind: 'closed', order: found });
        } else if (outcome === 'FAILED' || redirectStatus === 'failed') {
          setDeclined(DECLINED);
        }
        if (live) setLoaded({ kind: 'ready' });
      } catch (error) {
        if (!live) return;
        setLoaded({
          kind: 'error',
          message:
            error instanceof SessionApiError && error.status === 404
              ? 'We could not find that order on this device.'
              : error instanceof Error
                ? error.message
                : 'Something went wrong.',
        });
      }
    })();
    return () => {
      live = false;
    };
  }, [resumeOrderId, sessionKnown, redirectStatus, loadStoreBySlug]);

  // A fresh checkout of this browser's basket.
  const startRound = useRef(0);
  const begin = useCallback(async () => {
    const round = ++startRound.current;
    setLoaded({ kind: 'loading' });
    try {
      const cart = await getCart();
      if (cart.lines.length === 0) {
        if (round === startRound.current) setLoaded({ kind: 'empty' });
        return;
      }
      const started = await startCheckout();
      const [shop, needs] = await Promise.all([
        loadStoreBySlug(started.storefront?.slug),
        Promise.all(
          started.quote.lines.map((line) =>
            getRequirement(line.listingId)
              .then((found) => [line.listingId, found] as const)
              .catch(() => undefined),
          ),
        ),
      ]);
      if (round !== startRound.current) return;
      setCheckout(started);
      setStore(shop);
      setRequirements(new Map(needs.filter((entry) => entry !== undefined)));
      const delivers = shop !== undefined && deliversFrom(shop);
      const collects = shop === undefined || collectsFrom(shop);
      const preferred: Mode =
        started.fulfilment ??
        (wantedMode === 'DELIVERY' && delivers
          ? 'DELIVERY'
          : wantedMode === 'COLLECTION' && collects
            ? 'COLLECTION'
            : delivers
              ? 'DELIVERY'
              : 'COLLECTION');
      setModeState(preferred);
      if (started.deliveryAddress !== undefined) {
        setTyped({
          a1: started.deliveryAddress.line1,
          a2: started.deliveryAddress.line2 ?? '',
          town: started.deliveryAddress.town,
          pc: started.deliveryAddress.postcode,
        });
      }
      if (started.contact !== undefined) {
        setGuest({
          name: started.contact.name,
          email: started.contact.email,
          phone: started.contact.phone ?? '',
        });
      }
      if (started.fulfilment !== preferred) {
        const next = await setFulfilment(started.id, preferred).catch(() => undefined);
        if (next !== undefined && round === startRound.current) setCheckout(next);
      }
      if (round === startRound.current) setLoaded({ kind: 'ready' });
    } catch (error) {
      if (round !== startRound.current) return;
      if (
        error instanceof SessionApiError &&
        error.status === 400 &&
        /nothing in your basket/i.test(error.message)
      ) {
        setLoaded({ kind: 'empty' });
        return;
      }
      setLoaded({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Something went wrong. Try again.',
      });
    }
  }, [loadStoreBySlug, wantedMode]);

  useEffect(() => {
    if (resumeOrderId !== undefined || !sessionKnown) return;
    void begin();
  }, [resumeOrderId, sessionKnown, begin]);

  // The signed-in person's own details and saved addresses.
  useEffect(() => {
    if (!signedIn) {
      setAddresses([]);
      setProfileName(undefined);
      setProfilePhone(undefined);
      return undefined;
    }
    let live = true;
    getProfile()
      .then((profile) => {
        if (!live) return;
        setAddresses(profile.addresses);
        setProfileName(profile.displayName);
        setProfilePhone(profile.contacts.find((contact) => contact.channel === 'sms')?.value);
        const preferred = profile.addresses.find((a) => a.isDefault) ?? profile.addresses[0];
        if (preferred !== undefined) setAddressChoice(preferred.addressId);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [signedIn]);

  // What a signed-in person earns here. PENNY may be off: then there is nothing to show.
  const checkoutId = checkout?.id;
  const quoteTotal = checkout?.quote.totalMinor;
  useEffect(() => {
    if (!signedIn || checkoutId === undefined) {
      setEarn(undefined);
      return undefined;
    }
    let live = true;
    getPennyQuote(checkoutId)
      .then((quote) => {
        if (live) setEarn(quote.earn?.totalUnits);
      })
      .catch(() => {
        if (live) setEarn(undefined);
      });
    return () => {
      live = false;
    };
  }, [signedIn, checkoutId, quoteTotal]);

  // -- Who, and what needs an account ------------------------------------------------

  const needAcctLines = useMemo(
    () =>
      (checkout?.quote.lines ?? []).filter(
        (line) =>
          line.consultationId !== undefined ||
          requirements.get(line.listingId)?.reason === 'PRESCRIPTION_MEDICINE',
      ),
    [checkout, requirements],
  );
  const needAcct = needAcctLines.length > 0;

  // -- Delivery or collection --------------------------------------------------------

  const delivers = store !== undefined && deliversFrom(store);
  const collects = store === undefined || collectsFrom(store);

  const chooseMode = async (next: Mode): Promise<void> => {
    if (checkout === undefined || next === mode) return;
    setModeState(next);
    setBanner(undefined);
    try {
      setCheckout(await setFulfilment(checkout.id, next));
    } catch (error) {
      setBanner(error instanceof Error ? error.message : 'Something went wrong. Try again.');
    }
  };

  const savedAddress = addresses.find((a) => a.addressId === addressChoice);
  const usingSaved = signedIn && savedAddress !== undefined;
  const deliveryFields: AddressFields = usingSaved
    ? {
        a1: savedAddress.line1,
        a2: savedAddress.line2 ?? '',
        town: savedAddress.city,
        pc: savedAddress.postcode,
      }
    : typed;
  const deliveryPostcode = normalisePostcode(deliveryFields.pc);

  /** Asks the platform whether this shop delivers to the postcode. */
  const checkReach = useCallback(
    async (postcode: string): Promise<ReachState> => {
      if (checkout === undefined) return { status: 'idle' };
      const normalised = normalisePostcode(postcode);
      if (!UK_POSTCODE.test(normalised)) {
        const state: ReachState = { status: 'malformed', postcode: normalised };
        setReach(state);
        return state;
      }
      setReach({ status: 'checking', postcode: normalised });
      try {
        const found = await deliveryReach(checkout.storefrontId, normalised);
        const state: ReachState = {
          status: found.delivers ? 'yes' : 'no',
          postcode: normalised,
          reach: found,
        };
        setReach(state);
        return state;
      } catch (error) {
        const status = error instanceof SessionApiError ? error.status : 0;
        const state: ReachState = {
          status: status === 404 ? 'unknown' : status === 400 ? 'malformed' : 'failed',
          postcode: normalised,
        };
        setReach(state);
        return state;
      }
    },
    [checkout],
  );

  // Check the postcode as soon as it is a postcode — the frame shows the answer under the field.
  useEffect(() => {
    if (mode !== 'DELIVERY' || checkout === undefined) return undefined;
    if (!UK_POSTCODE.test(deliveryPostcode)) {
      setReach({ status: 'idle' });
      return undefined;
    }
    if (
      (reach.status === 'yes' || reach.status === 'no' || reach.status === 'checking') &&
      reach.postcode === deliveryPostcode
    )
      return undefined;
    const timer = setTimeout(() => void checkReach(deliveryPostcode), 500);
    return () => clearTimeout(timer);
  }, [mode, deliveryPostcode, checkout?.id]);

  // Once the shop delivers there and the address is complete, the platform quotes the fee for it.
  const addressKey = `${deliveryFields.a1}|${deliveryFields.a2}|${deliveryFields.town}|${deliveryPostcode}`;
  useEffect(() => {
    if (mode !== 'DELIVERY' || checkout === undefined) return undefined;
    if (reach.status !== 'yes' || reach.postcode !== deliveryPostcode) return undefined;
    if (Object.keys(addressErrors(deliveryFields)).length > 0) return undefined;
    if (quotedFor === addressKey) return undefined;
    const timer = setTimeout(() => {
      setAddress(checkout.id, {
        line1: deliveryFields.a1.trim(),
        line2: deliveryFields.a2.trim(),
        town: deliveryFields.town.trim(),
        postcode: deliveryFields.pc.trim().toUpperCase(),
      })
        .then((next) => {
          setCheckout(next);
          setQuotedFor(addressKey);
          setErrors((current) => ({ ...current, pc: undefined }));
        })
        .catch((error: unknown) => {
          setErrors((current) => ({
            ...current,
            pc: error instanceof Error ? error.message : 'We could not use that address.',
          }));
        });
    }, 400);
    return () => clearTimeout(timer);
  }, [mode, reach, addressKey, checkout?.id]);

  const feeKnown =
    mode === 'COLLECTION' ||
    (quotedFor === addressKey &&
      checkout?.deliveryAddress !== undefined &&
      reach.status === 'yes' &&
      reach.postcode === deliveryPostcode);

  const reachMessage = ((): string | undefined => {
    switch (reach.status) {
      case 'no':
        return `${store?.name ?? 'This pharmacy'} doesn't deliver to ${reach.reach.postcode}. You can collect instead.`;
      case 'unknown':
        return "We can't find that postcode.";
      case 'failed':
        return "We couldn't check this postcode right now. Try again.";
      case 'malformed':
        return 'Enter a valid UK postcode';
      default:
        return undefined;
    }
  })();

  // -- Payment: Stripe's Payment Element, when that is how this deployment is paid ----------------

  const usesStripe = config?.gateway === 'stripe' && config.publishableKey !== undefined;
  const paying = order !== undefined && finish === undefined;
  const figures: SummaryFigures | undefined =
    order !== undefined
      ? figuresOfOrder(order)
      : checkout !== undefined
        ? figuresOfQuote(checkout.quote, mode, feeKnown)
        : undefined;
  const amountMinor = figures?.feeKnown === true ? figures.totalMinor : figures?.itemsTotalMinor;
  const currency = (figures?.currency ?? 'GBP').toLowerCase();

  useEffect(() => {
    if (
      !usesStripe ||
      config.publishableKey === undefined ||
      amountMinor === undefined ||
      finish !== undefined
    )
      return undefined;
    const host = cardHost.current;
    if (host === null) return undefined;
    if (stripeRef.current !== undefined) {
      stripeRef.current.elements.update({ amount: Math.max(amountMinor, 1) });
      return undefined;
    }
    let live = true;
    setCardState('loading');
    loadStripe(config.publishableKey)
      .then((stripe) => {
        if (!live) return;
        const elements = stripe.elements({
          mode: 'payment',
          amount: Math.max(amountMinor, 1),
          currency,
          appearance: STRIPE_APPEARANCE,
        });
        const element = elements.create('payment', { layout: 'tabs' });
        element.mount(host);
        element.on('ready', () => setCardState('ready'));
        stripeRef.current = { stripe, elements };
      })
      .catch(() => {
        if (live) setCardState('failed');
      });
    return () => {
      live = false;
    };
  }, [usesStripe, config, amountMinor, currency, finish]);

  /** Polls the order until the webhook's outcome shows on it — never longer than the Android app waits. */
  const watch = useCallback(async (orderId: string): Promise<void> => {
    let last: Order | undefined;
    for (let attempt = 0; attempt < polling.attempts; attempt += 1) {
      await sleep(polling.delayMs);
      try {
        last = await getOrder(orderId);
      } catch {
        // A read that fails says nothing about the money. Keep waiting.
        continue;
      }
      setOrder(last);
      const outcome = paymentOutcome(last);
      if (outcome === 'PAID') {
        const tracking = await getOrderTracking(orderId).catch(() => undefined);
        setProc(undefined);
        setFinish({ kind: 'done', order: last, tracking });
        window.scrollTo({ top: 0 });
        return;
      }
      if (outcome === 'FAILED') {
        forgetAttempt(orderId);
        setProc(undefined);
        setDeclined(DECLINED);
        document.getElementById('sec-pay')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (outcome === 'CLOSED') {
        setProc(undefined);
        setFinish({ kind: 'closed', order: last });
        return;
      }
    }
    setProc(undefined);
    if (last === undefined) last = await getOrder(orderId).catch(() => undefined);
    if (last !== undefined) {
      setFinish({ kind: 'pending', order: last });
      window.scrollTo({ top: 0 });
    }
  }, []);

  /** Opens (or re-opens) the attempt and, for Stripe, confirms it with the card in the element. */
  const pay = useCallback(
    async (orderId: string): Promise<void> => {
      setDeclined(undefined);
      setBanner(undefined);
      setProc('Confirming your payment');
      let started;
      try {
        started = await startPayment(orderId, attemptKey(orderId));
      } catch (error) {
        setProc(undefined);
        setBanner(error instanceof Error ? error.message : 'We could not start the payment. Try again.');
        return;
      }
      if (usesStripe) {
        const handle = stripeRef.current;
        if (handle === undefined) {
          setProc(undefined);
          setBanner('The card form is not ready yet. Try again in a moment.');
          return;
        }
        const result = await handle.stripe.confirmPayment({
          elements: handle.elements,
          clientSecret: started.clientToken,
          redirect: 'if_required',
          confirmParams: {
            return_url: `${window.location.origin}${routes.checkout.path}?order=${encodeURIComponent(orderId)}`,
          },
        });
        if (result.error !== undefined) {
          setProc(undefined);
          setDeclined(
            result.error.type === 'card_error'
              ? DECLINED
              : {
                  title: "Your payment didn't go through",
                  copy: `${result.error.message ?? 'Check your details and try again.'} You haven't been charged.`,
                },
          );
          document.getElementById('sec-pay')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
      await watch(orderId);
    },
    [usesStripe, watch],
  );

  // Back from a Stripe redirect that went through: wait for the webhook, as after any payment.
  const resumedOnce = useRef(false);
  useEffect(() => {
    if (resumedOnce.current || order === undefined || finish !== undefined) return;
    if (redirectStatus !== 'succeeded' && redirectStatus !== 'processing') return;
    resumedOnce.current = true;
    setProc('Confirming your payment');
    void watch(order.id);
  }, [order, finish, redirectStatus, watch]);

  // -- Place order -----------------------------------------------------------------------

  const contactName = signedIn ? (profileName ?? memberName).trim() : guest.name.trim();
  const contactEmail = signedIn ? (session.user?.email ?? '') : guest.email.trim();
  const needsMemberName = signedIn && (profileName === undefined || profileName.trim() === '');

  const okDetails = signedIn
    ? !needsMemberName || memberName.trim().length > 1
    : !needAcct && Object.keys(detailErrors(guest)).length === 0;
  const okFulfil = mode === 'COLLECTION' || feeKnown;
  const payReady = config?.available === true && (config.gateway === 'fake' || usesStripe);

  const scrollTo = (id: string): void => {
    document.getElementById(id)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const place = async (): Promise<void> => {
    if (checkout === undefined || proc !== undefined) return;
    setBanner(undefined);
    const found: FieldErrors = {};
    let accountMissing = false;
    if (needAcct && !signedIn) accountMissing = true;
    if (!signedIn) Object.assign(found, detailErrors(guest));
    if (needsMemberName && memberName.trim().length < 2) found.name = 'Enter your full name';
    if (mode === 'DELIVERY') {
      if (!usingSaved) Object.assign(found, addressErrors(typed));
      if (found.pc === undefined && reachMessage !== undefined && reach.status !== 'checking')
        found.pc = reachMessage;
      if (found.pc === undefined && !feeKnown && reach.status !== 'yes')
        found.pc = 'We are still checking this address. Try again in a moment.';
    }
    // The shop delivers there but the fee is not quoted yet (the press beat the quote): quote it
    // now and stop, so the customer sees the delivery fee before anything is placed.
    if (
      mode === 'DELIVERY' &&
      Object.keys(found).length === 0 &&
      !accountMissing &&
      !feeKnown &&
      reach.status === 'yes'
    ) {
      try {
        const next = await setAddress(checkout.id, {
          line1: deliveryFields.a1.trim(),
          line2: deliveryFields.a2.trim(),
          town: deliveryFields.town.trim(),
          postcode: deliveryFields.pc.trim().toUpperCase(),
        });
        setCheckout(next);
        setQuotedFor(addressKey);
        setErrors({});
        flash('Delivery fee added. Check your total');
      } catch (error) {
        setErrors({ pc: error instanceof Error ? error.message : 'We could not use that address.' });
      }
      return;
    }
    setErrors(found);
    setAcctError(accountMissing);
    if (accountMissing || Object.keys(found).length > 0) {
      scrollTo(accountMissing || found.name || found.email || found.phone ? 'sec-details' : 'sec-fulfil');
      flash(
        accountMissing ? 'Log in or create an account to place this order' : 'Check the highlighted details',
      );
      return;
    }
    if (!payReady) {
      scrollTo('sec-pay');
      return;
    }
    // Stripe asks for the card to be checked before anything else happens.
    if (usesStripe) {
      const handle = stripeRef.current;
      if (handle === undefined) {
        setBanner('The card form is not ready yet. Try again in a moment.');
        return;
      }
      const checked = await handle.elements.submit();
      if (checked.error !== undefined) {
        scrollTo('sec-pay');
        return;
      }
    }

    setProc('Placing your order');
    try {
      await setContact(checkout.id, {
        name: contactName,
        email: contactEmail,
        ...((signedIn ? profilePhone : guest.phone) !== undefined
          ? { phone: (signedIn ? profilePhone : guest.phone) ?? '' }
          : {}),
      });
      const placed = await confirmCheckout(checkout.id);
      setPlacedRef({ id: placed.orderId, reference: placed.reference });
      // The address of this page is now the order's, so a reload or a bank's redirect comes back to it.
      window.history.replaceState(
        {},
        '',
        `${routes.checkout.path}?order=${encodeURIComponent(placed.orderId)}`,
      );
      const placedOrder = await getOrder(placed.orderId).catch(() => undefined);
      if (placedOrder !== undefined) setOrder(placedOrder);
      await pay(placed.orderId);
    } catch (error) {
      setProc(undefined);
      const refusal =
        error instanceof SessionApiError
          ? ((error.details as { refusal?: string }).refusal ?? (error.details as { code?: string }).code)
          : undefined;
      if (refusal === 'PRICE_CHANGED' || refusal === 'QUOTE_EXPIRED') {
        try {
          const fresh = await startCheckout();
          const change = priceChange(checkout.quote, fresh.quote);
          setCheckout(fresh);
          if (change === undefined && fresh.quote.totalMinor === checkout.quote.totalMinor) {
            // Only the quote's time ran out; the prices stand. Place it again.
            await place();
            return;
          }
          setPriceModal({
            ...(change !== undefined
              ? { name: change.name, oldMinor: change.oldMinor, nowMinor: change.nowMinor }
              : {}),
            totalMinor: fresh.quote.totalMinor,
            currency: fresh.quote.currency,
          });
        } catch (again) {
          setBanner(again instanceof Error ? again.message : 'Something went wrong. Try again.');
        }
        return;
      }
      setBanner(error instanceof Error ? error.message : 'Something went wrong. Try again.');
      window.scrollTo({ top: 0 });
    }
  };

  const onPlace = (): void => {
    if (order !== undefined) {
      void pay(order.id);
      return;
    }
    void place();
  };

  // -- Render ------------------------------------------------------------------------------

  const storeHref = store !== undefined ? `/storefronts/${store.slug}` : undefined;
  const shopName = store?.name ?? checkout?.storefront?.name ?? order?.storefront?.name ?? 'Your pharmacy';
  const shopInitials = initialsOf(shopName);

  if (finish !== undefined) {
    return (
      <Finished
        finish={finish}
        store={store}
        storeHref={storeHref}
        signedIn={signedIn}
        name={signedIn ? (profileName ?? session.user?.displayName) : guest.name || undefined}
        email={signedIn ? undefined : guest.email || undefined}
        earn={earn}
        testGateway={config?.gateway === 'fake'}
        onCheckAgain={() => {
          setFinish(undefined);
          setProc('Confirming your payment');
          void watch(finish.order.id);
        }}
      />
    );
  }

  if (loaded.kind === 'loading' || (!sessionKnown && resumeOrderId === undefined)) {
    return (
      <div className="jt-co-state">
        <h1 className="jt-co-h1">Checkout</h1>
        <p className="jt-co-muted" role="status">
          Getting your basket…
        </p>
      </div>
    );
  }

  if (loaded.kind === 'empty') {
    return (
      <div className="jt-co-state">
        <h1 className="jt-co-h1">Checkout</h1>
        <div className="jt-co-card jt-co-card--centre">
          <span className="jt-co-state__head">Your basket is empty</span>
          <span className="jt-co-muted">
            Add medicines and health products from a pharmacy, then check out here.
          </span>
          <AppLink to={routes.search.path} className="jt-co-pill">
            Find a product
          </AppLink>
        </div>
      </div>
    );
  }

  if (loaded.kind === 'error') {
    return (
      <div className="jt-co-state">
        <h1 className="jt-co-h1">Checkout</h1>
        <div className="jt-co-card jt-co-card--centre" role="alert">
          <span className="jt-co-state__head">We couldn&rsquo;t open checkout</span>
          <span className="jt-co-muted">{loaded.message}</span>
          <button
            type="button"
            className="jt-co-pill"
            onClick={() => (resumeOrderId === undefined ? void begin() : window.location.reload())}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (figures === undefined) return <div className="jt-co-state" />;

  const delMode = figures.fulfilment === 'DELIVERY';
  const modeLine = delMode
    ? `Delivery${store?.deliveryEtaLabel !== undefined ? ` · ${store.deliveryEtaLabel}` : ''}`
    : `Collection${store?.collectionEtaLabel !== undefined ? ` · ${store.collectionEtaLabel}` : ''}`;
  const placeLabel = paying
    ? `Pay ${money(figures.totalMinor, figures.currency)}`
    : figures.feeKnown
      ? `Place order · ${money(figures.totalMinor, figures.currency)}`
      : 'Place order';
  const placeDisabled = !payReady || proc !== undefined;
  const placeButton = !narrow ? (
    <button type="button" className="jt-co-place" disabled={placeDisabled} onClick={onPlace}>
      {placeLabel}
    </button>
  ) : null;
  const supplied = suppliedBy(store, shopName);

  return (
    <>
      <div className="jt-co">
        <div className="jt-co__main">
          {storeHref !== undefined && !paying ? (
            <AppLink to={storeHref} className="jt-co-back">
              <Svg d={ICON.back} size={15} width={2.6} />
              Back to basket
            </AppLink>
          ) : (
            <AppLink to={routes.orders.path} className="jt-co-back">
              <Svg d={ICON.back} size={15} width={2.6} />
              Your orders
            </AppLink>
          )}
          <div className="jt-co-title">
            <h1 className="jt-co-h1">Checkout</h1>
            <span className="jt-co-sub">Your order from {shopName}</span>
          </div>

          {banner !== undefined ? (
            <div className="jt-co-alert" role="alert">
              <Svg d={ICON.warn} size={18} stroke="#a81f1f" width={2.4} />
              <span>{banner}</span>
            </div>
          ) : null}

          {paying && placedRef !== undefined ? (
            <section aria-labelledby="h-placed" className="jt-co-card">
              <div className="jt-co-sechead">
                <StepNumber n={1} done />
                <h2 id="h-placed" className="jt-co-h2">
                  Order {placedRef.reference} is placed
                </h2>
              </div>
              <p className="jt-co-muted">
                It&rsquo;s waiting for payment. Nothing is charged until the payment goes through, and the
                order is confirmed only when the payment provider confirms it.
              </p>
            </section>
          ) : (
            <>
              <section id="sec-details" aria-labelledby="h-details" className="jt-co-card">
                <div className="jt-co-sechead">
                  <StepNumber n={1} done={okDetails} />
                  <h2 id="h-details" className="jt-co-h2">
                    Your details
                  </h2>
                </div>
                {signedIn && session.user !== undefined ? (
                  <>
                    <div className="jt-co-me">
                      <span className="jt-co-me__av">{initialsOf(profileName ?? session.user.email)}</span>
                      <span className="jt-co-me__text">
                        <span className="jt-co-me__name">{profileName ?? 'Your account'}</span>
                        <span className="jt-co-me__line">
                          {session.user.email}
                          {profilePhone !== undefined ? ` · ${profilePhone}` : ''}
                        </span>
                      </span>
                      <button
                        type="button"
                        className="jt-co-ghost"
                        onClick={() => {
                          void session.signOut().then(() => flash('Signed out'));
                        }}
                      >
                        Not you?
                      </button>
                    </div>
                    {needsMemberName ? (
                      <div className="jt-co-grid">
                        <Field
                          label="Full name"
                          value={memberName}
                          onChange={setMemberName}
                          error={errors.name}
                          autoComplete="name"
                          placeholder="As it appears on your ID"
                          wide
                        />
                      </div>
                    ) : null}
                  </>
                ) : needAcct ? (
                  <div className={`jt-co-acct${acctError ? ' jt-co-acct--err' : ''}`} role="alert">
                    <div className="jt-co-acct__row">
                      <span aria-hidden="true" className="jt-co-acct__icon">
                        <Svg d={ICON.warn} size={17} stroke="#5c3206" width={2.4} />
                      </span>
                      <span className="jt-co-acct__text">
                        <span className="jt-co-acct__head">You need an account for this order</span>
                        <span className="jt-co-acct__copy">
                          {needAcctLines.map((line) => line.title).join(', ')}{' '}
                          {needAcctLines.length === 1 ? 'needs' : 'need'} a consultation or prescription. Your
                          prescriber or pharmacist needs a record to check your treatment safely.
                        </span>
                      </span>
                    </div>
                    <div className="jt-co-acct__actions">
                      <LoginLink className="jt-co-acct__create">Create an account</LoginLink>
                      <LoginLink className="jt-co-acct__login">Log in</LoginLink>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="jt-co-have">
                      <span className="jt-co-have__text">
                        <span className="jt-co-have__head">Have an account?</span>
                        <span className="jt-co-have__copy">
                          Log in to use your saved addresses and earn PENNY.
                        </span>
                      </span>
                      <LoginLink className="jt-co-have__btn">Log in</LoginLink>
                    </div>
                    <div className="jt-co-guesthead">
                      <span className="jt-co-guesthead__h">Or continue as a guest</span>
                      <span className="jt-co-muted">We&rsquo;ll send order updates to these details.</span>
                    </div>
                    <div className="jt-co-grid">
                      <Field
                        label="Full name"
                        value={guest.name}
                        onChange={(name) => {
                          setGuest({ ...guest, name });
                          setErrors({ ...errors, name: undefined });
                        }}
                        error={errors.name}
                        autoComplete="name"
                        placeholder="As it appears on your ID"
                        wide
                      />
                      <Field
                        label="Email"
                        value={guest.email}
                        onChange={(email) => {
                          setGuest({ ...guest, email });
                          setErrors({ ...errors, email: undefined });
                        }}
                        error={errors.email}
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        placeholder="name@example.com"
                      />
                      <Field
                        label="Mobile number"
                        value={guest.phone}
                        onChange={(phone) => {
                          setGuest({ ...guest, phone });
                          setErrors({ ...errors, phone: undefined });
                        }}
                        error={errors.phone}
                        type="tel"
                        autoComplete="tel"
                        inputMode="tel"
                        placeholder="07…"
                      />
                    </div>
                  </>
                )}
              </section>

              <section id="sec-fulfil" aria-labelledby="h-fulfil" className="jt-co-card">
                <div className="jt-co-sechead">
                  <StepNumber n={2} done={okFulfil} />
                  <h2 id="h-fulfil" className="jt-co-h2">
                    {delivers && collects ? 'Delivery or collection' : delivers ? 'Delivery' : 'Collection'}
                  </h2>
                </div>
                {delivers && collects ? (
                  <div role="radiogroup" aria-label="Delivery or collection" className="jt-co-modes">
                    {(
                      [
                        ['DELIVERY', 'Delivery', store?.deliveryEtaLabel ?? 'Fee quoted for your address'],
                        [
                          'COLLECTION',
                          'Collection',
                          `Free${store?.collectionEtaLabel !== undefined ? ` · ${store.collectionEtaLabel}` : ''}`,
                        ],
                      ] as const
                    ).map(([id, label, sub]) => (
                      <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={mode === id}
                        className={`jt-co-mode${mode === id ? ' jt-co-mode--on' : ''}`}
                        onClick={() => void chooseMode(id)}
                      >
                        <span className="jt-co-mode__label">{label}</span>
                        <span className="jt-co-mode__sub">{sub}</span>
                      </button>
                    ))}
                  </div>
                ) : null}

                {mode === 'DELIVERY' ? (
                  <>
                    <div className="jt-co-block">
                      <span className="jt-co-label">Delivery address</span>
                      {signedIn && addresses.length > 0 ? (
                        <div role="radiogroup" aria-label="Saved addresses" className="jt-co-stack">
                          {[...addresses, undefined].map((address) => {
                            const value = address?.addressId ?? 'new';
                            const on = addressChoice === value;
                            return (
                              <button
                                key={value}
                                type="button"
                                role="radio"
                                aria-checked={on}
                                className={`jt-co-opt${on ? ' jt-co-opt--on' : ''}`}
                                onClick={() => {
                                  setAddressChoice(value);
                                  setErrors({ ...errors, a1: undefined, town: undefined, pc: undefined });
                                }}
                              >
                                <span className="jt-co-opt__icon">
                                  <Svg
                                    d={address === undefined ? ICON.plus : ICON.home}
                                    size={18}
                                    stroke="#5b2ede"
                                  />
                                </span>
                                <span className="jt-co-opt__text">
                                  <span className="jt-co-opt__label">
                                    {address === undefined
                                      ? 'A new address'
                                      : (address.label ?? 'Saved address')}
                                  </span>
                                  {address !== undefined ? (
                                    <span className="jt-co-opt__sub">
                                      {[address.line1, address.line2, address.city, address.postcode]
                                        .filter((part) => part !== undefined && part !== '')
                                        .join(', ')}
                                    </span>
                                  ) : null}
                                </span>
                                <Radio on={on} />
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                      {!usingSaved ? (
                        <div className="jt-co-grid jt-co-grid--addr">
                          <Field
                            label="Address line 1"
                            value={typed.a1}
                            onChange={(a1) => {
                              setTyped({ ...typed, a1 });
                              setErrors({ ...errors, a1: undefined });
                            }}
                            error={errors.a1}
                            autoComplete="address-line1"
                            placeholder="House number and street"
                            wide
                          />
                          <Field
                            label="Address line 2 (optional)"
                            value={typed.a2}
                            onChange={(a2) => setTyped({ ...typed, a2 })}
                            autoComplete="address-line2"
                            placeholder="Flat, floor or building"
                            wide
                          />
                          <Field
                            label="Town or city"
                            value={typed.town}
                            onChange={(town) => {
                              setTyped({ ...typed, town });
                              setErrors({ ...errors, town: undefined });
                            }}
                            error={errors.town}
                            autoComplete="address-level2"
                          />
                          <Field
                            label="Postcode"
                            value={typed.pc}
                            onChange={(pc) => {
                              setTyped({ ...typed, pc: pc.toUpperCase() });
                              setErrors({ ...errors, pc: undefined });
                            }}
                            error={errors.pc ?? (reach.status !== 'malformed' ? reachMessage : undefined)}
                            autoComplete="postal-code"
                            placeholder="e.g. NR2 4TE"
                            onBlur={() => {
                              // Only a postcode not already answered: re-asking on every blur would
                              // flip a known answer back to "checking" under a Place order press.
                              const pc = normalisePostcode(typed.pc);
                              const answered = reach.status !== 'idle' && reach.postcode === pc;
                              if (UK_POSTCODE.test(pc) && !answered) void checkReach(typed.pc);
                            }}
                          />
                        </div>
                      ) : errors.pc !== undefined || reachMessage !== undefined ? (
                        <span className="jt-co-field__err" role="alert">
                          {errors.pc ?? reachMessage}
                        </span>
                      ) : null}
                      {reach.status === 'checking' ? (
                        <span className="jt-co-muted" role="status">
                          Checking {store?.name ?? 'the pharmacy'} delivers there…
                        </span>
                      ) : reach.status === 'yes' && reach.postcode === deliveryPostcode ? (
                        <span className="jt-co-ok" role="status">
                          <Svg d={ICON.check} size={14} stroke="#156b49" width={2.6} />
                          {store?.name ?? 'The pharmacy'} delivers to {reach.reach.postcode}
                          {feeKnown
                            ? ` · ${figures.deliveryFeeMinor > 0 ? `${money(figures.deliveryFeeMinor, figures.currency)} delivery` : 'free delivery'}`
                            : ''}
                        </span>
                      ) : null}
                    </div>
                    <div className="jt-co-block">
                      <span className="jt-co-label">When</span>
                      <div role="radiogroup" aria-label="Delivery time" className="jt-co-when">
                        <button
                          type="button"
                          role="radio"
                          aria-checked="true"
                          className="jt-co-opt jt-co-opt--on"
                        >
                          <span className="jt-co-opt__text">
                            <span className="jt-co-opt__label">As soon as possible</span>
                            {store?.deliveryEtaLabel !== undefined ? (
                              <span className="jt-co-opt__sub">{store.deliveryEtaLabel}</span>
                            ) : null}
                          </span>
                          <Radio on />
                        </button>
                        <div
                          role="radio"
                          aria-checked="false"
                          aria-disabled="true"
                          className="jt-co-opt jt-co-opt--off"
                        >
                          <span className="jt-co-opt__text">
                            <span className="jt-co-opt__label">Schedule</span>
                            <span className="jt-co-opt__sub">
                              Pick a 1-hour window · <Soon />
                            </span>
                          </span>
                          <Radio on={false} />
                        </div>
                      </div>
                    </div>
                    <label className="jt-co-field jt-co-field--wide">
                      <span className="jt-co-field__label">
                        Note for the courier <span className="jt-co-opt-label">(optional)</span> <Soon />
                      </span>
                      <input
                        type="text"
                        disabled
                        placeholder="e.g. Flat 2, ring the top bell"
                        maxLength={140}
                        className="jt-co-input"
                      />
                    </label>
                  </>
                ) : (
                  <>
                    <div className="jt-co-collect">
                      <span className="jt-co-tile jt-co-tile--lg">{shopInitials}</span>
                      <span className="jt-co-collect__text">
                        <span className="jt-co-collect__name">{shopName}</span>
                        {shopAddress(store) !== undefined ? (
                          <span className="jt-co-collect__addr">{shopAddress(store)}</span>
                        ) : null}
                        {store?.collectionEtaLabel !== undefined ? (
                          <span className="jt-co-collect__ready">{store.collectionEtaLabel}</span>
                        ) : null}
                      </span>
                    </div>
                    <p className="jt-co-muted">
                      Show your collection code from Orders when you collect. The pharmacist may ask to see
                      photo ID for some medicines.
                    </p>
                  </>
                )}

                <div className="jt-co-subst">
                  <span className="jt-co-label">
                    If an item is out of stock <Soon />
                  </span>
                  <div
                    role="radiogroup"
                    aria-label="Substitutions"
                    aria-disabled="true"
                    className="jt-co-chips"
                  >
                    <span role="radio" aria-checked="false" aria-disabled="true" className="jt-co-chip">
                      Pharmacist picks a similar item
                    </span>
                    <span role="radio" aria-checked="false" aria-disabled="true" className="jt-co-chip">
                      Remove it and refund me
                    </span>
                  </div>
                </div>
              </section>
            </>
          )}

          <section id="sec-pay" aria-labelledby="h-pay" className="jt-co-card">
            <div className="jt-co-sechead">
              <StepNumber n={paying ? 2 : 3} done={false} />
              <h2 id="h-pay" className="jt-co-h2">
                Payment
              </h2>
              <span className="jt-co-enc">
                <Svg d={ICON.lock} size={14} width={2.2} />
                Encrypted
              </span>
            </div>
            {declined !== undefined ? (
              <div role="alert" className="jt-co-declined">
                <Svg d={ICON.warn} size={18} stroke="#a81f1f" width={2.4} />
                <span className="jt-co-declined__text">
                  <span className="jt-co-declined__head">{declined.title}</span>
                  <span className="jt-co-declined__copy">{declined.copy}</span>
                </span>
              </div>
            ) : null}
            {config === undefined ? (
              <p className="jt-co-muted" role="status">
                Checking how this site takes payment…
              </p>
            ) : !config.available || config.gateway === 'none' ? (
              <p className="jt-co-alert" role="alert">
                Payments aren&rsquo;t switched on here yet, so orders can&rsquo;t be placed on the website.
              </p>
            ) : config.gateway === 'stripe' && config.publishableKey === undefined ? (
              <p className="jt-co-alert" role="alert">
                Card payments aren&rsquo;t available on this site yet, so orders can&rsquo;t be placed on the
                website.
              </p>
            ) : (
              <>
                <div role="radiogroup" aria-label="Payment method" className="jt-co-stack">
                  {signedIn ? (
                    <div
                      role="radio"
                      aria-checked="false"
                      aria-disabled="true"
                      className="jt-co-pay jt-co-opt--off"
                    >
                      <span className="jt-co-pay__mark jt-co-pay__mark--visa">CARD</span>
                      <span className="jt-co-opt__text">
                        <span className="jt-co-opt__label">Saved cards</span>
                        <span className="jt-co-opt__sub">
                          <Soon />
                        </span>
                      </span>
                      <Radio on={false} />
                    </div>
                  ) : null}
                  <button type="button" role="radio" aria-checked="true" className="jt-co-pay jt-co-opt--on">
                    <span className="jt-co-pay__mark">CARD</span>
                    <span className="jt-co-opt__text">
                      <span className="jt-co-opt__label">
                        {config.gateway === 'fake' ? 'Test payment' : 'Debit or credit card'}
                      </span>
                      <span className="jt-co-opt__sub">
                        {config.gateway === 'fake'
                          ? 'No card is taken — this site is not taking card payments yet'
                          : 'Card details go straight to Stripe, never to us'}
                      </span>
                    </span>
                    <Radio on />
                  </button>
                  {(
                    [
                      ['Apple Pay', 'Pay', 'jt-co-pay__mark--apple'],
                      ['Google Pay', 'G Pay', 'jt-co-pay__mark--google'],
                    ] as const
                  ).map(([label, mark, cls]) => (
                    <div
                      key={label}
                      role="radio"
                      aria-checked="false"
                      aria-disabled="true"
                      className="jt-co-pay jt-co-opt--off"
                    >
                      <span className={`jt-co-pay__mark ${cls}`}>{mark}</span>
                      <span className="jt-co-opt__text">
                        <span className="jt-co-opt__label">{label}</span>
                        <span className="jt-co-opt__sub">
                          <Soon />
                        </span>
                      </span>
                      <Radio on={false} />
                    </div>
                  ))}
                </div>
                {usesStripe ? (
                  <>
                    <div ref={cardHost} className="jt-co-card-el" aria-label="Card details" />
                    {cardState === 'loading' ? (
                      <span className="jt-co-muted" role="status">
                        Loading the secure card form…
                      </span>
                    ) : cardState === 'failed' ? (
                      <span className="jt-co-field__err" role="alert">
                        The secure card form could not load. Check your connection and reload the page.
                      </span>
                    ) : null}
                    {config.testMode ? (
                      <p className="jt-co-note">
                        Stripe test mode: no real money moves. Use a Stripe test card such as 4242 4242 4242
                        4242.
                      </p>
                    ) : null}
                    {signedIn ? (
                      <div role="checkbox" aria-checked="false" aria-disabled="true" className="jt-co-save">
                        <span aria-hidden="true" className="jt-co-save__box" />
                        <span>Save this card for next time</span> <Soon />
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="jt-co-note">
                    The order is confirmed only when the payment provider confirms the payment. On this site
                    that confirmation does not come yet, so a test order waits for payment in Orders.
                  </p>
                )}
              </>
            )}
          </section>
        </div>

        <Summary
          shopName={shopName}
          shopInitials={shopInitials}
          storeHref={paying ? undefined : storeHref}
          modeLine={modeLine}
          figures={figures}
          signedIn={signedIn}
          earn={earn}
          guestNudge={!signedIn && !paying}
          promo={!paying}
          placeButton={placeButton}
          supplied={supplied}
        />
      </div>

      {narrow ? (
        <div className="jt-co-bar">
          <span className="jt-co-bar__total">
            <span className="jt-co-bar__k">Total</span>
            <span className="jt-co-bar__v">{totalLabel(figures)}</span>
          </span>
          <button type="button" className="jt-co-bar__btn" disabled={placeDisabled} onClick={onPlace}>
            {paying ? 'Pay now' : 'Place order'}
          </button>
        </div>
      ) : null}

      {proc !== undefined ? (
        <>
          <div className="jt-co-scrim" />
          <div
            role="alertdialog"
            aria-live="assertive"
            aria-label="Processing payment"
            className="jt-co-proc"
          >
            <span aria-hidden="true" className="jt-co-spin" />
            <span className="jt-co-proc__title">{proc}</span>
            <span className="jt-co-proc__copy">
              Please don&rsquo;t close this page. Your bank may ask you to confirm the payment.
            </span>
          </div>
        </>
      ) : null}

      {priceModal !== undefined ? (
        <>
          <div className="jt-co-scrim" onClick={() => setPriceModal(undefined)} />
          <div role="dialog" aria-modal="true" aria-labelledby="pc-title" className="jt-co-modal">
            <div className="jt-co-modal__head">
              <span aria-hidden="true" className="jt-co-modal__icon">
                <Svg d={ICON.warn} size={20} stroke="#b25e09" width={2.2} />
              </span>
              <span className="jt-co-modal__text">
                <span id="pc-title" className="jt-co-modal__title">
                  A price has changed
                </span>
                <span className="jt-co-modal__copy">
                  {shopName} updated a price while you were checking out. You haven&rsquo;t been charged yet.
                </span>
              </span>
            </div>
            <div className="jt-co-modal__box">
              {priceModal.name !== undefined &&
              priceModal.oldMinor !== undefined &&
              priceModal.nowMinor !== undefined ? (
                <div className="jt-co-modal__row">
                  <span className="jt-co-modal__name">{priceModal.name}</span>
                  <span>
                    <span className="jt-co-modal__old">
                      {money(priceModal.oldMinor, priceModal.currency)}
                    </span>
                    <b>{money(priceModal.nowMinor, priceModal.currency)}</b>
                  </span>
                </div>
              ) : null}
              <div className="jt-co-modal__row jt-co-modal__row--total">
                <span>New total</span>
                <b>{money(priceModal.totalMinor, priceModal.currency)}</b>
              </div>
            </div>
            <div className="jt-co-modal__actions">
              {storeHref !== undefined ? (
                <AppLink to={storeHref} className="jt-co-modal__back">
                  Back to basket
                </AppLink>
              ) : null}
              <button
                type="button"
                className="jt-co-modal__pay"
                onClick={() => {
                  setPriceModal(undefined);
                  void place();
                }}
              >
                Pay {money(priceModal.totalMinor, priceModal.currency)}
              </button>
            </div>
          </div>
        </>
      ) : null}

      {toast !== '' ? (
        <div role="status" className={`jt-co-toast${narrow ? ' jt-co-toast--high' : ''}`}>
          {toast}
        </div>
      ) : null}
    </>
  );
}

// -- Done / still confirming ------------------------------------------------------------------

function Finished({
  finish,
  store,
  storeHref,
  signedIn,
  name,
  email,
  earn,
  testGateway,
  onCheckAgain,
}: {
  readonly finish: Finish;
  readonly store: Store | undefined;
  readonly storeHref: string | undefined;
  readonly signedIn: boolean;
  readonly name: string | undefined;
  readonly email: string | undefined;
  readonly earn: number | undefined;
  readonly testGateway: boolean;
  readonly onCheckAgain: () => void;
}): JSX.Element {
  const order = finish.order;
  const shop = shopOf(order);
  const isD = order.fulfilment === 'DELIVERY';
  const trackHref = `${routes.orders.path}?order=${encodeURIComponent(order.id)}`;
  const consulted = order.lines.some((line) => line.consultationId !== undefined);
  const placed = new Date(order.placedAt);
  const placedLabel = `${placed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, ${placed.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  const figures = figuresOfOrder(order);

  if (finish.kind !== 'done') {
    const pending = finish.kind === 'pending';
    return (
      <div className="jt-co-done">
        <div className="jt-co-done__hero">
          <h1 className="jt-co-done__title">
            {pending ? "We're still confirming your payment" : 'This order is closed'}
          </h1>
          <p className="jt-co-done__sub">
            {pending
              ? `Order ${order.reference} is placed. It's confirmed once the payment provider tells us the payment went through, which can take a moment. You don't need to pay again.`
              : `Order ${order.reference} was cancelled or refunded, so there is nothing to pay.`}
          </p>
          {pending && testGateway ? (
            <p className="jt-co-note">
              This site is not taking card payments yet: no card was taken, and the order waits for payment in
              Orders.
            </p>
          ) : null}
        </div>
        <section aria-label="Order status" className="jt-co-card">
          <div className="jt-co-actions">
            <AppLink to={trackHref} className="jt-co-pill">
              View order
            </AppLink>
            {pending ? (
              <button type="button" className="jt-co-pill jt-co-pill--soft" onClick={onCheckAgain}>
                Check again
              </button>
            ) : null}
          </div>
        </section>
      </div>
    );
  }

  const steps: readonly Step[] =
    finish.tracking !== undefined ? stepsOf(order, finish.tracking.journey) : stepsOf(order, [order.status]);
  const where = isD
    ? [order.deliveryAddress?.line1, order.deliveryAddress?.town, order.deliveryAddress?.postcode]
        .filter((part) => part !== undefined && part !== '')
        .join(', ')
    : [shop.name, shopAddress(store)].filter((part) => part !== undefined && part !== '').join(', ');
  const label = isD ? store?.deliveryEtaLabel : store?.collectionEtaLabel;

  return (
    <div className="jt-co-done">
      <div className="jt-co-done__hero">
        <span className="jt-co-done__tick">
          <Svg d={ICON.check} size={34} stroke="#156b49" width={2.8} />
        </span>
        <h1 className="jt-co-done__title">{thanksTitle(name)}</h1>
        <p className="jt-co-done__sub">
          {consulted
            ? "Your payment has gone through. The pharmacy's prescriber now reviews your answers before anything is sent."
            : 'Your payment has gone through.'}
        </p>
      </div>

      <section aria-label="Order status" className="jt-co-card">
        <div className="jt-co-done__shop">
          <span className="jt-co-tile jt-co-tile--done" style={{ background: shop.tile }}>
            {shop.initials}
          </span>
          <span className="jt-co-done__shoptext">
            <span className="jt-co-done__store">{shop.name}</span>
            <span className="jt-co-muted">
              Order {order.reference} · {placedLabel}
            </span>
          </span>
          <span className="jt-co-badge">{isD ? 'DELIVERY' : 'COLLECTION'}</span>
        </div>
        <div className="jt-co-eta">
          <span className="jt-co-eta__icon">
            <Svg d={isD ? ICON.truck : ICON.store} size={20} stroke="#5b2ede" />
          </span>
          <span className="jt-co-eta__text">
            <span className="jt-co-eta__head">{isD ? 'DELIVERING TO' : 'COLLECT FROM'}</span>
            <span className="jt-co-eta__main">{where}</span>
            {label !== undefined ? <span className="jt-co-eta__where">{label}</span> : null}
          </span>
        </div>
        <ol className="jt-co-steps">
          {steps.map((step, index) => (
            <li key={`${step.label}-${String(index)}`} className={`jt-co-step jt-co-step--${step.state}`}>
              <span className="jt-co-step__rail">
                <span className="jt-co-step__dot">
                  {step.state === 'done' ? <Svg d={ICON.check} size={12} stroke="#fff" width={3.6} /> : null}
                </span>
                {index < steps.length - 1 ? <span className="jt-co-step__line" /> : null}
              </span>
              <span className="jt-co-step__text">
                <span className="jt-co-step__label">{step.label}</span>
                {step.sub !== '' ? <span className="jt-co-step__sub">{step.sub}</span> : null}
              </span>
            </li>
          ))}
        </ol>
        <div className="jt-co-actions">
          <AppLink to={trackHref} className="jt-co-pill">
            Track order
          </AppLink>
          {storeHref !== undefined ? (
            <AppLink to={storeHref} className="jt-co-pill jt-co-pill--soft">
              Continue shopping
            </AppLink>
          ) : null}
        </div>
      </section>

      {!signedIn ? (
        <section aria-label="Create an account" className="jt-co-join">
          <div className="jt-co-join__row">
            <span className="jt-co-join__text">
              <span className="jt-co-join__head">Save your details for next time</span>
              <span className="jt-co-join__copy">
                Create an account{email !== undefined ? ` with ${email}` : ''} to follow your orders on any
                device and reorder in a few taps.
              </span>
            </span>
            <span aria-hidden="true" className="jt-co-join__icon">
              <Svg d={ICON.person} size={26} stroke="#fff" />
            </span>
          </div>
          <span className="jt-site-contents" onClickCapture={() => rememberReturnPath(trackHref)}>
            <AppLink to={routes.logIn.path} className="jt-co-join__btn">
              Create an account
            </AppLink>
          </span>
        </section>
      ) : earn !== undefined && earn > 0 ? (
        <section aria-label="Rewards earned" className="jt-co-rewards">
          <div className="jt-co-reward">
            <span className="jt-co-reward__text">
              <span className="jt-co-reward__head">+{earn} PENNY</span>
              <span className="jt-co-muted">Pending until your order qualifies</span>
            </span>
            <span aria-hidden="true" className="jt-co-reward__icon">
              <Svg d={ICON.star} size={22} stroke="#c0396a" />
            </span>
          </div>
        </section>
      ) : null}

      <section aria-label="Order details" className="jt-co-card jt-co-details">
        <span className="jt-co-details__head">Order details</span>
        {order.lines.map((line) => (
          <div key={line.id} className="jt-co-details__row">
            <span>
              {line.quantity} × {line.title}
            </span>
            <span className="jt-co-details__v">{money(line.lineTotalMinor, order.currency)}</span>
          </div>
        ))}
        <div className="jt-co-details__rule" />
        {rowsOf(figures).map((row) => (
          <div key={row.k} className="jt-co-details__row">
            <span>{row.k}</span>
            <span className={row.green === true ? 'jt-co-row__green' : 'jt-co-details__v'}>{row.v}</span>
          </div>
        ))}
        <div className="jt-co-details__total">
          <span>Paid</span>
          <span>{money(order.totalMinor, order.currency)}</span>
        </div>
        <span className="jt-co-details__help">
          Need to change something?{' '}
          <AppLink to={helpTopicPath('orders')} className="jt-co-details__helplink">
            Get help with this order
          </AppLink>
        </span>
      </section>
    </div>
  );
}
